import type {
  AIResponse,
  VehicleInfo,
  AIDiagnosisCause,
  AIFixSteps,
  ChatSession,
} from "../types";

// Use VPSReporting server proxy for OpenAI requests (security improvement)
const VPS_SERVER_BASE_URL =
  process.env.REACT_APP_API_URI || "http://localhost:5001";

interface OpenAIChatResponse {
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
    finish_reason?: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

interface OpenAIChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

interface OpenAIChatRequest {
  messages: OpenAIChatMessage[];
  temperature?: number;
  max_tokens?: number;
}

const SYSTEM_INSTRUCTION = `You are an automotive diagnostic AI. Follow these rules STRICTLY:

1. **MULTIPLE CHOICE QUESTIONS:** Ask questions with 3-4 clear, distinct answer options. Examples:
   - "What type of noise does the engine make when starting?" with options: ["Clicking sound", "Grinding noise", "No unusual noise", "Whining sound"] ✓
   - "When does the problem occur?" with options: ["Only when cold", "Only when hot", "All the time", "Only under load"] ✓
   - "What color is the smoke?" with options: ["White smoke", "Black smoke", "Blue smoke", "No visible smoke"] ✓

2. **ONE QUESTION AT A TIME:** Ask only ONE question per response. Do not ask multiple questions.

3. **PROGRESSIVE DIAGNOSIS:** After 4-6 questions, provide a diagnosis. Do not ask the same question twice.

4. **CONVERSATION FLOW:**
   - First question: Ask about the most obvious symptom with multiple choice options
   - Follow-up questions: Based on the user's previous answers, ask more specific questions
   - Final step: Provide diagnosis with 2-3 most likely causes

5. **HANDLE OFF-TOPIC RESPONSES:** If a user provides information that is not related to vehicle diagnostics, ask them to focus on vehicle symptoms and repeat your previous question.

6. **CRITICAL - JSON OUTPUT ONLY:** Your response must be ONLY a valid JSON object. No explanations, no additional text.

Response Schema:
{
  "type": "question" | "diagnosis",
  "questionText": "string (only if type is question)",
  "options": ["option1", "option2", "option3", "option4"] (only if type is question, 3-4 options),
  "causes": [
    {
      "cause": "string",
      "likelihood": "Most Likely" | "Likely",
      "reason": "string"
    }
  ] (only if type is diagnosis)
}

Examples:
{"type": "question", "questionText": "What type of noise does the engine make when starting?", "options": ["Clicking sound", "Grinding noise", "No unusual noise", "Whining sound"]}
{"type": "diagnosis", "causes": [{"cause": "Faulty starter motor", "likelihood": "Most Likely", "reason": "Engine cranks but won't start"}]}`;

const FIX_STEPS_SCHEMA = `{
  "type": "fix",
  "steps": ["string"]
}`;

const cleanResponse = (text: string): string => {
  // Remove common AI thinking patterns
  let cleaned = text
    .replace(
      /^(Let me|I'll|I can|Based on|Looking at|I see that|I notice that|From the|Given that|It appears that|It seems that|I think|I believe|I would say|In my opinion|To answer your question|To explain|To clarify|To provide|Here's|Here is)/i,
      "",
    )
    .replace(/^(The|This|A|An)\s+/i, (match) => match.toLowerCase())
    .replace(/\s+/g, " ")
    .trim();

  // If the response starts with a lowercase letter, capitalize it
  if (cleaned && /^[a-z]/.test(cleaned)) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  return cleaned;
};

const parseJsonResponse = <T>(text: string): T => {
  try {
    // Clean up the response text
    let cleanedText = text.trim();

    // Remove markdown code blocks if present
    cleanedText = cleanedText.replace(/^```json\s*|```\s*$/g, "").trim();

    // Try to extract JSON from the response
    const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      [cleanedText] = jsonMatch;
    }

    // Parse JSON
    const parsed = JSON.parse(cleanedText);
    return parsed;
  } catch (error) {
    console.error("Failed to parse JSON response:", text, error);
    throw new Error(`Invalid JSON response: ${error}`);
  }
};

// Make API call via VPSReporting server proxy (secure)
const makeOpenAIRequest = async (
  messages: OpenAIChatMessage[],
  options: { temperature?: number; max_tokens?: number } = {},
): Promise<string> => {
  const payload: OpenAIChatRequest = {
    messages,
    temperature: options.temperature || 0.7,
    max_tokens: options.max_tokens || 1000,
  };

  try {
    // Making request to VPS server proxy for OpenAI
    const response = await fetch(
      `${VPS_SERVER_BASE_URL}/find-a-fix/openai/chat`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Include authorization header if user is logged in (VPSReporting session)
          Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("VPS server proxy error:", response.status, errorBody);
      throw new Error(
        `Server proxy error: ${response.status} ${response.statusText} - ${errorBody}`,
      );
    }

    const data: OpenAIChatResponse = await response.json();
    const content = data.choices[0]?.message?.content || "";

    if (!content) {
      console.warn("Empty response from OpenAI via proxy:", data);
      throw new Error("Empty response from OpenAI service");
    }

    return content;
  } catch (error) {
    console.error("Error calling OpenAI via server proxy:", error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to communicate with OpenAI service: ${error}`);
  }
};

// Chat session class for maintaining conversation state
export class OpenAIChatSession {
  private messages: OpenAIChatMessage[] = [];

  constructor(systemInstruction: string) {
    this.messages.push({
      role: "system",
      content: systemInstruction,
    });
  }

  async sendMessage(message: { message: string }): Promise<{ text: string }> {
    // Add user message
    this.messages.push({
      role: "user",
      content: message.message,
    });

    try {
      // Get response from OpenAI
      const response = await makeOpenAIRequest(this.messages);

      // Add assistant response to history
      this.messages.push({
        role: "assistant",
        content: response,
      });

      return { text: response };
    } catch (error) {
      console.error("Error in chat session:", error);
      throw error;
    }
  }

  getHistory(): OpenAIChatMessage[] {
    return [...this.messages];
  }
}

// Image analysis for vehicle fault detection
export const analyzePartImagesForFault = async (
  fileContent: string,
  mimeType: string,
): Promise<string> => {
  try {
    const messages: OpenAIChatMessage[] = [
      {
        role: "system",
        content:
          "You are an automotive diagnostic expert. Analyze the provided vehicle part image and describe any visible faults, damage, or wear patterns. Be specific and technical in your analysis.",
      },
      {
        role: "user",
        content: `Analyze this vehicle part image for any faults or damage. The image is in ${mimeType} format. Image data: ${fileContent.substring(0, 100)}... (truncated for brevity)`,
      },
    ];

    const response = await makeOpenAIRequest(messages, { max_tokens: 500 });
    return cleanResponse(response);
  } catch (error) {
    console.error("Error analyzing image:", error);
    return "Unable to analyze image at this time. Please describe the issue verbally.";
  }
};

// Start a new chat session
export const startChatSession = async (
  vehicleInfo: VehicleInfo,
): Promise<{ chat: ChatSession; response: AIResponse }> => {
  try {
    // Create initial prompt with vehicle information
    const initialPrompt = `Vehicle Information:
- Make: ${vehicleInfo.make}
- Model: ${vehicleInfo.model}
- Year: ${vehicleInfo.year}
- Problem Description: ${vehicleInfo.faultDescription}
- DTC Codes: ${vehicleInfo.dtcCodes || "None provided"}

Start the diagnostic conversation by asking the first multiple choice question to help identify the root cause of the issue.`;

    const chat = new OpenAIChatSession(SYSTEM_INSTRUCTION);
    const response = await chat.sendMessage({ message: initialPrompt });

    const parsedResponse = parseJsonResponse<AIResponse>(response.text);
    return { chat, response: parsedResponse };
  } catch (error) {
    console.error("Error starting chat session:", error);
    throw new Error("Failed to start diagnostic conversation with OpenAI");
  }
};

// Continue an existing chat session
export const continueChatSession = async (
  chat: ChatSession,
  message: string,
): Promise<AIResponse> => {
  try {
    const response = await chat.sendMessage({ message });
    return parseJsonResponse<AIResponse>(response.text);
  } catch (error) {
    console.error("Error continuing chat session:", error);
    throw new Error("Failed to continue conversation with OpenAI");
  }
};

// Get detailed fix steps for a specific cause
export const getFixSteps = async (
  vehicleInfo: VehicleInfo,
  cause: AIDiagnosisCause,
): Promise<AIFixSteps> => {
  try {
    const prompt = `Vehicle: ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}
Problem: ${vehicleInfo.faultDescription}
DTC Codes: ${vehicleInfo.dtcCodes || "None"}

Identified Cause: ${cause.cause}
Likelihood: ${cause.likelihood}
Reason: ${cause.reason}

Provide detailed step-by-step repair instructions for this specific issue. Return ONLY JSON in this format:
${FIX_STEPS_SCHEMA}

Make the steps specific, actionable, and include safety warnings where appropriate.`;

    const messages: OpenAIChatMessage[] = [
      {
        role: "system",
        content:
          "You are an automotive repair expert. Provide detailed, step-by-step repair instructions in JSON format only.",
      },
      {
        role: "user",
        content: prompt,
      },
    ];

    const response = await makeOpenAIRequest(messages, { max_tokens: 1500 });
    return parseJsonResponse<AIFixSteps>(response);
  } catch (error) {
    console.error("Error getting fix steps:", error);
    throw new Error("Failed to get repair instructions from OpenAI");
  }
};

// Get detailed information about a specific cause
export const getCauseDetails = async (
  vehicleInfo: VehicleInfo,
  cause: AIDiagnosisCause,
): Promise<string> => {
  try {
    const prompt = `Explain in detail why "${cause.cause}" is ${cause.likelihood.toLowerCase()} for a ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model} with the problem: "${vehicleInfo.faultDescription}"

Provide technical details about this diagnosis.`;

    const messages: OpenAIChatMessage[] = [
      {
        role: "system",
        content:
          "You are an automotive diagnostic expert. Provide detailed technical explanations about vehicle problems.",
      },
      {
        role: "user",
        content: prompt,
      },
    ];

    const response = await makeOpenAIRequest(messages, { max_tokens: 800 });
    return cleanResponse(response);
  } catch (error) {
    console.error("Error getting cause details:", error);
    return "Unable to provide detailed explanation at this time.";
  }
};

// Look up DTC code information
export const lookupDtcCode = async (code: string): Promise<string> => {
  try {
    const prompt = `Provide detailed information about diagnostic trouble code (DTC): ${code}

Include:
- What the code means
- Common causes
- Symptoms
- Basic diagnostic steps

Be concise but comprehensive.`;

    const messages: OpenAIChatMessage[] = [
      {
        role: "system",
        content:
          "You are an automotive diagnostic expert specializing in trouble codes. Provide accurate DTC information.",
      },
      {
        role: "user",
        content: prompt,
      },
    ];

    const response = await makeOpenAIRequest(messages, { max_tokens: 600 });
    return cleanResponse(response);
  } catch (error) {
    console.error("Error looking up DTC code:", error);
    return `Unable to lookup DTC code ${code} at this time.`;
  }
};
